import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { ENTRYPOINT } from '@core/endpoint';
import { Pagination } from '@core/interface';

const SEEDING_PALLET_URL = `${ENTRYPOINT}seeding_cycles/pallets_v2/`;
const SEEDING_URL = `${ENTRYPOINT}seeding_cycles/seedings/`;

export interface PalletRequest {
  seeding: number;
  territory: string;
}
export interface SeedingRequest {
  start_date: string;
  end_date: string;
  pallets: number;
  schooled: number;
  frozen: number;
  lost: number;
  is_archived: boolean;
  species: number;
}

export interface Seeding extends SeedingRequest {
  id: number;
}
export interface Pallet extends PalletRequest {
  id: number;
}

export interface SeedingParams {
  offset: number;
  limit: number;
  is_archived?: boolean;
  species?: number | null;
}

@Injectable({
  providedIn: 'root',
})
export class SeedingService {
  private readonly http = inject(HttpClient);

  getSeedingById(id: number): Observable<Seeding> {
    return this.http.get<Seeding>(`${SEEDING_URL}${id}/`);
  }

  loadSeedings(params: SeedingParams): Observable<Pagination<Seeding>> {
    let httpParams = new HttpParams()
      .set('offset', params.offset)
      .set('limit', params.limit);

    if (params.is_archived !== undefined) {
      httpParams = httpParams.set('is_archived', params.is_archived);
    }

    if (params.species || params.species === 0) {
      httpParams = httpParams.set('species', params.species);
    }

    return this.http.get<Pagination<Seeding>>(SEEDING_URL, {
      params: httpParams,
    });
  }

  createSeeding(seeding: SeedingRequest): Observable<Seeding> {
    return this.http.post<Seeding>(SEEDING_URL, seeding);
  }

  updateSeeding(seeding: Seeding): Observable<Seeding> {
    return this.http.put<Seeding>(`${SEEDING_URL}${seeding.id}/`, seeding);
  }

  deleteSeeding(id: number): Observable<unknown> {
    return this.http.delete<unknown>(`${SEEDING_URL}${id}/`);
  }

  getPalletById(id: number): Observable<Pallet> {
    return this.http.get<Pallet>(`${SEEDING_PALLET_URL}${id}/`);
  }

  loadPallets(): Observable<Pagination<Pallet>> {
    return this.http.get<Pagination<Pallet>>(SEEDING_PALLET_URL);
  }

  createPallets(pallets: PalletRequest[]): Observable<Pallet[]> {
    return this.http.post<Pallet[]>(SEEDING_PALLET_URL, pallets);
  }

  updatePallets(pallets: Pallet[]): Observable<Pallet[]> {
    return this.http.put<Pallet[]>(SEEDING_PALLET_URL, pallets);
  }

  deletePallets(pallets: { id: number }[]): Observable<unknown> {
    return this.http.delete<unknown>(SEEDING_PALLET_URL, {
      body: pallets,
    });
  }
}
